// src/utils/shopValidation.js - Système de validation des boutiques avec détection des numéros surtaxés

/**
 * Vérifie si un numéro est surtaxé (commence par 08 ou +33 8)
 * @param {string} phone - Le numéro de téléphone à vérifier
 * @returns {boolean} - true si le numéro est surtaxé
 */
const isPremiumNumber = (phone) => {
  if (!phone) return false;
  
  const cleaned = phone.replace(/[\s.-]/g, '');
  
  // Vérifier si ça commence par 08
  if (cleaned.startsWith('08')) {
    return true;
  }
  
  // Vérifier si ça commence par +33 8 ou 0033 8
  if (cleaned.startsWith('+338') || cleaned.startsWith('00338')) {
    return true;
  }
  
  return false;
};

/**
 * Valide un shop et retourne la liste des erreurs de validation
 * @param {Object} shop - La boutique à valider
 * @returns {Array<string>} - Liste des erreurs de validation
 */
export const validateShop = (shop) => {
  const errors = [];

  // Validation des champs essentiels
  if (!shop.name || shop.name.trim().length === 0) {
    errors.push('Nom de la boutique manquant');
  } else if (shop.name.trim().length < 3) {
    errors.push('Nom de la boutique trop court (min. 3 caractères)');
  }

  if (!shop.categories || shop.categories.length === 0) {
    errors.push('Aucune catégorie sélectionnée');
  }

  if (!shop.description || shop.description.trim().length === 0) {
    errors.push('Description manquante');
  } else if (shop.description.trim().length < 100) {
    errors.push('Description trop courte (min. 100 caractères)');
  } else if (shop.description.length > 550) {
    errors.push('Description trop longue (max. 550 caractères)');
  }

  if (!shop.mainPhone || shop.mainPhone.trim().length === 0) {
    errors.push('Téléphone principal (SAV) manquant');
  } else if (isPremiumNumber(shop.mainPhone)) {
    errors.push('⚠️ Le téléphone principal est un numéro surtaxé (08)');
  }

  if (!shop.logo || shop.logo.trim().length === 0) {
    errors.push('Logo manquant');
  }

  // Validation de l'adresse principale
  if (!shop.mainAddress || shop.mainAddress.trim().length === 0) {
    errors.push('Adresse principale manquante');
  }

  // Validation des avis - vérifier si le tableau existe et n'est pas vide
  if (!shop.avis || shop.avis.length === 0) {
    errors.push('Aucun avis client');
  }

  // Vérifier tous les téléphones pour détecter les numéros surtaxés
  if (shop.phones && shop.phones.length > 0) {
    const premiumPhones = shop.phones.filter(phone => 
      isPremiumNumber(phone.normalized || phone.original)
    );
    
    if (premiumPhones.length > 0) {
      errors.push(`⚠️ ${premiumPhones.length} numéro(s) surtaxé(s) détecté(s) (08)`);
    }
  }

  return errors;
};

/**
 * Retourne les erreurs pour une étape spécifique
 * @param {Object} shop - La boutique à valider
 * @param {string} stepId - L'ID de l'étape
 * @returns {Array<string>} - Liste des erreurs pour cette étape
 */
export const validateStep = (shop, stepId) => {
  const allErrors = validateShop(shop);
  
  const stepFieldErrors = {
    'essential': [
      'Nom de la boutique manquant',
      'Nom de la boutique trop court (min. 3 caractères)',
      'Aucune catégorie sélectionnée',
      'Description manquante',
      'Description trop courte (min. 20 caractères)',
      'Description trop longue (max. 150 caractères)',
      'Téléphone principal (SAV) manquant',
      '⚠️ Le téléphone principal est un numéro surtaxé (08)',
      'Logo manquant'
    ],
    'complementary': [
      'Adresse principale manquante'
    ],
    'avis': [
      'Aucun avis client'
    ],
    'seo': []
  };

  const stepErrors = stepFieldErrors[stepId] || [];
  return allErrors.filter(error => {
    // Vérifier les erreurs exactes
    if (stepErrors.includes(error)) return true;
    
    // Vérifier les erreurs avec pattern (pour les numéros surtaxés détectés)
    if (error.includes('numéro(s) surtaxé(s) détecté(s)')) {
      return stepId === 'essential';
    }
    
    return false;
  });
};

/**
 * Vérifie si une étape est complète
 * @param {Object} shop - La boutique à valider
 * @param {string} stepId - L'ID de l'étape
 * @returns {boolean} - true si l'étape est complète
 */
export const isStepComplete = (shop, stepId) => {
  const errors = validateStep(shop, stepId);
  return errors.length === 0;
};

/**
 * Vérifie si la boutique peut être validée
 * @param {Object} shop - La boutique à valider
 * @returns {boolean} - true si tous les champs requis sont remplis
 */
export const canValidateShop = (shop) => {
  const errors = validateShop(shop);
  return errors.length === 0;
};

// Export également la fonction de vérification
export { isPremiumNumber };